import { parseISO, isValid, differenceInCalendarMonths } from "date-fns";
import {
    DocumentTaxonomyExtraction,
    ExtractionStatus,
} from "@common/types/financialDocument";
import { DocumentCategory } from "@common/types/documentCategory";
import { doesDocumentAccountNumberMatch } from "@common/helpers/accountNumberMatch";
import { getCoveredMonthKeys } from "./documentRetrievalDates";

const OPTIMISTIC_EXTRACTING_WINDOW_MS = 10_000;

export type DocVerificationField = "category" | "account" | "period";

export type MismatchReason =
    | "mismatched_category"
    | "mismatched_account"
    | "mismatched_period"
    | "mismatched_period_start"
    | "duplicate_period";

export type DocVerificationStatus =
    | {
          kind: "verified" | "extracting";
          invalidFields: ReadonlySet<DocVerificationField>;
      }
    | {
          kind: "missing";
          field?: DocVerificationField;
          invalidFields: ReadonlySet<DocVerificationField>;
      }
    | {
          kind: "mismatch";
          reason: MismatchReason;
          invalidFields: ReadonlySet<DocVerificationField>;
      };

export interface DocForVerification {
    id: number;
    createdAt?: Date | string | null;
    taxonomyExtractionStatus: ExtractionStatus;
    taxonomyExtraction: DocumentTaxonomyExtraction | null;
    documentCategoryId?: string | null;
    documentCategory?: Pick<
        DocumentCategory,
        "id" | "label" | "documentTaxonomy" | "parentCategoryId" | "workspaceId"
    > | null;
}

interface VerificationContext {
    monthKeys: ReadonlySet<string>;
    occupiedMonths: ReadonlyMap<string, number>;
    expectedCategoryIds?: ReadonlySet<string>;
    // Candidate account numbers to match against (account number mask).
    // Empty = no constraint (skip the check).
    expectedAccountNumberCandidates?: readonly string[];
    // True while the target account is still being resolved. See
    // `getDocVerificationStatus` for the would-be-verified defer.
    expectedAccountNumberCandidatesLoading?: boolean;
    now?: Date;
    // Optional override for getCoveredMonthKeys to allow callers to
    // pass a memoized version and avoid recomputation across calls.
    getCoveredMonthKeysFn?: (doc: DocForVerification) => string[];
}

interface FieldCheck {
    isPresent: boolean;
    isMatching: boolean;
    mismatchReason?: MismatchReason;
}

const FIELD_MISMATCH_REASONS: Record<DocVerificationField, MismatchReason> = {
    category: "mismatched_category",
    account: "mismatched_account",
    period: "mismatched_period",
};

const FIELD_ORDER: readonly DocVerificationField[] = [
    "category",
    "account",
    "period",
];

function checkCategory(
    doc: DocForVerification,
    expectedCategoryIds: ReadonlySet<string> | undefined,
): FieldCheck {
    const docCategoryId = doc.documentCategoryId ?? doc.documentCategory?.id;
    if (!docCategoryId) {
        return { isPresent: false, isMatching: false };
    }
    if (!expectedCategoryIds || expectedCategoryIds.size === 0) {
        return { isPresent: true, isMatching: true };
    }
    if (expectedCategoryIds.has(docCategoryId)) {
        return { isPresent: true, isMatching: true };
    }
    const parentId = doc.documentCategory?.parentCategoryId ?? null;
    const parentMatches =
        parentId !== null && expectedCategoryIds.has(parentId);
    return { isPresent: true, isMatching: parentMatches };
}

function checkAccount(
    doc: DocForVerification,
    expectedAccountNumberCandidates: readonly string[],
): FieldCheck {
    const accountNumber = doc.taxonomyExtraction?.accountNumber ?? null;
    const isPresent = !!accountNumber && accountNumber.trim().length > 0;
    if (!isPresent) {
        return { isPresent: false, isMatching: false };
    }
    return {
        isPresent: true,
        isMatching: doesDocumentAccountNumberMatch(
            doc,
            expectedAccountNumberCandidates,
        ),
    };
}

// Any overlap with an already-occupied month is treated as `duplicate_period`.
// `.find()` intentionally stops at the first covered key in the task's month
// set — if that key is occupied by a different doc, the entire document is
// rejected even if it also covers uncontested months.
function checkPeriod(
    doc: DocForVerification,
    ctx: VerificationContext,
): FieldCheck {
    const periodEndDate = doc.taxonomyExtraction?.periodEndDate ?? null;
    if (!periodEndDate) {
        return { isPresent: false, isMatching: false };
    }
    const periodStartDate = doc.taxonomyExtraction?.periodStartDate ?? null;

    const resolveCoveredKeys = ctx.getCoveredMonthKeysFn ?? getCoveredMonthKeys;
    const coveredKeys = resolveCoveredKeys(doc);
    const relevantKey = coveredKeys.find((k) => ctx.monthKeys.has(k));
    if (!relevantKey) {
        return { isPresent: true, isMatching: false };
    }

    const occupantId = ctx.occupiedMonths.get(relevantKey);
    const isDuplicate = occupantId !== undefined && occupantId !== doc.id;
    if (isDuplicate) {
        return {
            isPresent: true,
            isMatching: false,
            mismatchReason: "duplicate_period",
        };
    }

    if (periodStartDate) {
        const startParsed = parseISO(periodStartDate);
        const endParsed = parseISO(periodEndDate);
        if (isValid(startParsed) && isValid(endParsed)) {
            const monthDiff = differenceInCalendarMonths(
                endParsed,
                startParsed,
            );
            if (monthDiff < 0) {
                return {
                    isPresent: true,
                    isMatching: false,
                    mismatchReason: "mismatched_period_start",
                };
            }
            if (monthDiff > 1) {
                const coveredTaskMonths = coveredKeys.filter((k) =>
                    ctx.monthKeys.has(k),
                );
                if (coveredTaskMonths.length < 2) {
                    return {
                        isPresent: true,
                        isMatching: false,
                        mismatchReason: "mismatched_period_start",
                    };
                }
            }
        }
    }

    return { isPresent: true, isMatching: true };
}

function toDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
        return null;
    }
    const parsed = value instanceof Date ? value : parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isWithinOptimisticWindow(
    createdAt: Date | string | null | undefined,
    now: Date,
): boolean {
    const created = toDate(createdAt);
    if (!created) {
        return false;
    }
    return now.getTime() - created.getTime() < OPTIMISTIC_EXTRACTING_WINDOW_MS;
}

// Resolution priority:
//   1. All fields valid -> verified (even mid-extraction).
//   2. NOT_STARTED within the optimistic window -> extracting.
//   3. EXTRACTING -> extracting.
//   4. Any present-but-mismatched field (in field order) -> mismatch <field>.
//   5. Any empty field(s) -> missing <field> or missing info, in field order.
export function getDocVerificationStatus(
    doc: DocForVerification,
    ctx: VerificationContext,
): DocVerificationStatus {
    const checks: Record<DocVerificationField, FieldCheck> = {
        category: checkCategory(doc, ctx.expectedCategoryIds),
        account: checkAccount(doc, ctx.expectedAccountNumberCandidates ?? []),
        period: checkPeriod(doc, ctx),
    };

    const invalidFields = new Set<DocVerificationField>();
    for (const field of FIELD_ORDER) {
        const c = checks[field];
        if (!c.isPresent || !c.isMatching) {
            invalidFields.add(field);
        }
    }

    // While the mask is loading, `checkAccount` skips and a divergent doc
    // would otherwise pass all checks; defer until it lands so we don't flash
    // verified → mismatch. Other field issues still surface immediately.
    if (invalidFields.size === 0) {
        if (ctx.expectedAccountNumberCandidatesLoading) {
            return { kind: "extracting", invalidFields };
        }
        return { kind: "verified", invalidFields };
    }

    const now = ctx.now ?? new Date();
    const status = doc.taxonomyExtractionStatus;
    if (
        status === ExtractionStatus.NOT_STARTED &&
        isWithinOptimisticWindow(doc.createdAt, now)
    ) {
        return { kind: "extracting", invalidFields };
    }
    if (status === ExtractionStatus.IN_PROGRESS) {
        return { kind: "extracting", invalidFields };
    }

    for (const field of FIELD_ORDER) {
        const c = checks[field];
        if (c.isPresent && !c.isMatching) {
            return {
                kind: "mismatch",
                reason: c.mismatchReason ?? FIELD_MISMATCH_REASONS[field],
                invalidFields,
            };
        }
    }

    const missingFields = FIELD_ORDER.filter(
        (field) => !checks[field].isPresent,
    );
    return {
        kind: "missing",
        field: missingFields.length === 1 ? missingFields[0] : undefined,
        invalidFields,
    };
}

export function toExpectedCategorySet(
    expectedCategoryIds: readonly string[] | undefined,
): ReadonlySet<string> | undefined {
    if (!expectedCategoryIds || expectedCategoryIds.length === 0) {
        return undefined;
    }
    return new Set(expectedCategoryIds);
}

export function isRetryableVerification(
    verification: DocVerificationStatus,
): boolean {
    return (
        verification.kind !== "verified" && verification.kind !== "extracting"
    );
}

export function getNextOptimisticExpiryMs(
    docs: ReadonlyArray<
        Pick<DocForVerification, "taxonomyExtractionStatus" | "createdAt">
    >,
    now: Date = new Date(),
): number | null {
    let nextDelay: number | null = null;
    for (const doc of docs) {
        if (doc.taxonomyExtractionStatus !== ExtractionStatus.NOT_STARTED) {
            continue;
        }
        const created = toDate(doc.createdAt);
        if (!created) {
            continue;
        }
        const elapsed = now.getTime() - created.getTime();
        const remaining = OPTIMISTIC_EXTRACTING_WINDOW_MS - elapsed;
        if (remaining <= 0) {
            continue;
        }
        if (nextDelay === null || remaining < nextDelay) {
            nextDelay = remaining;
        }
    }
    return nextDelay;
}
